import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  TextField,
  Button,
  Typography,
  Box,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  OutlinedInput,
  Checkbox,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import LinkIcon from "@mui/icons-material/Link";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchClients,
  addClient,
  updateClient,
  deleteClient,
} from "./../store/features/client/clientSlice";
import SnackNotification from "../components/common/SnackNotification";
import { getVendors } from "../store/features/vendors/vendorSlice";
import SearchBar from "../components/common/SearchBar";
import { base_url } from "../api/api-url";
import LayoutRowsEditor from "../components/Client/LayoutRowsEditor";
import { SERVICE_OPTIONS } from "../utility/serviceOptions";

const Client = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const clients = useSelector((state) => state.clientSlice.items);
  const vendors = useSelector((state) => state.vendors.vendors) || [];
  const [logoFile, setLogoFile] = useState(null);
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    control,
    watch,
    formState: { errors },
  } = useForm();
  const [editingId, setEditingId] = useState(null);
  const [layout, setLayout] = useState([]);
  const layoutTypeValue = watch("layoutType", "simple");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [filteredClients, setFilteredClients] = useState([]);
  const [clusterFilter, setClusterFilter] = useState("all");

  const servicesOptions = SERVICE_OPTIONS;

  const clusterOptions = [
    { value: "", label: "None" },
    { value: "cluster1", label: "Cluster 1" },
    { value: "cluster2", label: "Cluster 2" },
  ];

  const layoutTypeOptions = [
    { value: "simple", label: "Simple" },
    { value: "advanced", label: "Advanced" },
  ];

  const clusterFilterOptions = [
    { value: "all", label: "All Clusters" },
    { value: "cluster1", label: "Cluster 1" },                                                                                          
    { value: "cluster2", label: "Cluster 2" },
  ];
                                                              
  const formFields = [
    { type: "text", name: "name", label: "Client Name" },
    { type: "text", name: "contactPerson", label: "Person Name" },                          
    { type: "text", name: "designation", label: "Designation" },                                                                          
    { type: "text", name: "phone", label: "Phone Number" },                                       
    {
      type: "select",
      name: "layoutType",
      label: "Layout Type",
      options: layoutTypeOptions,
      rules: {},
    },
    {
      type: "multi-select",
      name: "services",
      label: "Services",
      options: servicesOptions,
    },
    { type: "multi-select", name: "vendor", label: "Vendor", options: vendors },
    {
      type: "select",
      name: "cluster",
      label: "Cluster",
      options: clusterOptions,
      rules: {},
    },

    // Optional fields (no validation)
    { type: "text", name: "website", label: "Website", rules: {} },
    { type: "text", name: "gst", label: "GST No.", rules: {} },

    {
      type: "text",
      name: "email",
      label: "Email Address",
      rules: {
        required: "Email is required",
        pattern: { value: /^\S+@\S+$/, message: "Invalid email" },
      },
    },
    { type: "text", name: "location", label: "Location" },
  ];

  useEffect(() => {
    dispatch(getVendors());
    dispatch(fetchClients());
  }, [dispatch]);

  useEffect(() => {
    setFilteredClients(clients);
  }, [clients]);

  const handleSnackClose = () => setSnack({ ...snack, open: false });

  const onSubmit = async (data) => {
    try {
      let logoUrl = control._formValues.logo || "";
      if (logoFile) {
        const formDataUpload = new FormData();
        formDataUpload.append("file", logoFile);
        const res = await fetch(`${base_url}/upload`, {
          method: "POST",
          body: formDataUpload,
        });
        if (!res.ok) throw new Error("Failed to upload file");
        const uploadResult = await res.json();
        if (uploadResult.success) logoUrl = uploadResult.data.url;
      }

      const { layout: _ignoredLayout, ...formValues } = data;
      const isAdvanced = data.layoutType === "advanced";
      const payload = {
        ...formValues,
        logo: logoUrl,
        layoutType: data.layoutType || "simple",
        services: isAdvanced ? [] : data.services || [],
        ...(isAdvanced ? { layout } : {}),
        vendor: (data.vendor || [])
          .map((id) => {
            const vendorObj = vendors.find((v) => v._id === id);
            return vendorObj ? { _id: vendorObj._id, name: vendorObj.name } : null;
          })
          .filter(Boolean),
      };

      if (editingId) {
        await dispatch(updateClient({ id: editingId, data: payload })).unwrap();
        setSnack({
          open: true,
          message: "Client updated successfully!",
          severity: "success",
        });
      } else {
        await dispatch(addClient(payload)).unwrap();
        setSnack({
          open: true,
          message: "Client added successfully!",
          severity: "success",
        });
      }

      reset({ layoutType: "simple" });
      setLayout([]);
      setEditingId(null);
      setSelectedFileName("");
      setLogoFile(null);
      setOpenDialog(false);
      dispatch(fetchClients());
    } catch (error) {
      setSnack({
        open: true,
        message: error.message || "Operation failed",
        severity: "error",
      });
    }
  };

  const handleEdit = (index) => {
    const client = displayedClients[index];
    if (!client) return;

    Object.keys(client).forEach((key) => {
      if (key === "layout") return; 
      if (key === "services" || key === "vendor") {
        setValue(key, client[key] || []);
      } else {
        setValue(key, client[key]);
      }
    });

    if (Array.isArray(client.vendor)) {
      setValue(
        "vendor",
        client.vendor.map((v) => (typeof v === "object" ? v._id : v))
      );
    }

    setValue("layoutType", client.layoutType || "simple");
    setLayout(client.layoutType === "advanced" ? client.layout || [] : []);

    setEditingId(client._id);
    setSelectedFileName(client.logo ? client.logo.split("/").pop() : "");
    setOpenDialog(true);
  };

  const handleDelete = async (index) => {
    const client = displayedClients[index];
    if (!client) return;

    try {
      await dispatch(deleteClient(client._id)).unwrap();
      setSnack({
        open: true,
        message: "Client deleted successfully!",
        severity: "success",
      });
      dispatch(fetchClients());
    } catch (error) {
      setSnack({
        open: true,
        message: error.message || "Delete failed",
        severity: "error",
      });
    }
  };

  const handleCloseDialog = () => {
    reset({ layoutType: "simple" });
    setLayout([]);
    setEditingId(null);
    setSelectedFileName("");
    setOpenDialog(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFileName(file.name);
    setLogoFile(file);
  };

  const handleSearch = (searchedData) => {
    setFilteredClients(searchedData);
    setPage(0);
  };

  const handleClusterFilterChange = (value) => {
    setClusterFilter(value);
    setPage(0);
  };

  const displayedClients = useMemo(() => {
    if (clusterFilter === "all") return filteredClients;
    return filteredClients.filter((client) => client.cluster === clusterFilter);
  }, [filteredClients, clusterFilter]);

  return (
    <Stack spacing={4}>
      <SnackNotification
        open={snack.open}
        message={snack.message}
        severity={snack.severity}
        handleClose={handleSnackClose}
      />

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { bgcolor: "#212529", color: "#fff", borderRadius: 2 },
        }}
      >
        <DialogTitle sx={{ borderBottom: "1px solid #343a40", fontWeight: "bold" }}>
          {editingId ? "Edit Client" : "Create Client"}
        </DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <DialogContent dividers>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              {formFields.map(({ type, name, label, rules, options }) => {
                // Advanced clients use the layout tree editor instead of the
                    // flat services multi-select — rendered separately below.
                if (name === "services" && layoutTypeValue === "advanced") {
                  return null;
                }

                if (type === "text") {
                  return (
                    <div key={name}>
                      <Typography variant="subtitle2" sx={{ color: "#adb5bd", mb: 0.5 }}>
                        {label}
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        {...register(name, rules !== undefined ? rules : { required: `${label} is required` })}
                        error={!!errors[name]}
                        helperText={errors[name]?.message}
                        sx={{
                          bgcolor: "#343a40",
                          input: { color: "#fff" },
                          borderRadius: 1,
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": { borderColor: "#6c757d" },
                            "&:hover fieldset": { borderColor: "#adb5bd" },
                            "&.Mui-focused fieldset": { borderColor: "#90caf9" },
                          },
                          "& .MuiFormHelperText-root": { color: "#f44336" },
                        }}
                      />
                    </div>
                  );
                }

                if (type === "select") {
                  return (
                    <FormControl key={name} fullWidth size="small">
                      <Typography variant="subtitle2" sx={{ color: "#adb5bd", mb: 0.5 }}>
                        {label}
                      </Typography>
                      <Controller
                        name={name}
                        control={control}
                        defaultValue={name === "layoutType" ? "simple" : ""}
                        render={({ field }) => (
                          <Select {...field} sx={{ bgcolor: "#343a40", color: "#fff" }}>
                            {options.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                    </FormControl>
                  );
                }

                if (type === "multi-select") {
                  if (name === "vendor") {
                    return (
                      <FormControl key={name} fullWidth size="small">
                        <Typography variant="subtitle2" sx={{ color: "#adb5bd", mb: 0.5 }}>
                          {label}
                        </Typography>
                        <Controller
                          name={name}
                          control={control}
                          defaultValue={[]}
                          render={({ field }) => (
                            <Select
                              multiple
                              {...field}
                              input={<OutlinedInput />}
                              renderValue={(selected) => {
                                const selectedVendors = options.filter(
                                  (opt) => selected.includes(opt._id)
                                );
                                return selectedVendors.map((v) => v.name).join(", ");
                              }}
                              sx={{ bgcolor: "#343a40", color: "#fff" }}
                            >
                              {options.map((option) => (
                                <MenuItem key={option._id} value={option._id}>
                                  <Checkbox checked={field.value.includes(option._id)} />
                                  <Typography>{option.name}</Typography>
                                </MenuItem>
                              ))}
                            </Select>
                          )}
                        />
                      </FormControl>
                    );
                  } else {
                    return (
                      <FormControl key={name} fullWidth size="small">
                        <Typography variant="subtitle2" sx={{ color: "#adb5bd", mb: 0.5 }}>
                          {label}
                        </Typography>
                        <Controller
                          name={name}
                          control={control}
                          defaultValue={[]}
                          render={({ field }) => (
                            <Select
                              multiple
                              {...field}
                              input={<OutlinedInput />}
                              renderValue={(selected) =>
                                selected.length > 0 ? selected.join(", ") : "Select..."
                              }
                              sx={{ bgcolor: "#343a40", color: "#fff" }}
                            >
                              {options.map((option) => (
                                <MenuItem key={option} value={option}>
                                  <Checkbox checked={field.value.includes(option)} />
                                  <Typography>{option}</Typography>
                                </MenuItem>
                              ))}
                            </Select>
                          )}
                        />
                      </FormControl>
                    );
                  }
                }

                return null;
              })}

              {layoutTypeValue === "advanced" && (
                <Box sx={{ gridColumn: "1 / -1" }}>
                  <Typography variant="subtitle2" sx={{ color: "#adb5bd", mb: 0.5 }}>
                    Layout
                  </Typography>
                  <LayoutRowsEditor
                    key={editingId || "new"}
                    nodes={layout}
                    onChange={setLayout}
                    serviceOptions={servicesOptions}
                  />
                </Box>
              )}

              {/* Logo Upload */}
              <FormControl fullWidth>
                <Typography variant="subtitle2" sx={{ color: "#adb5bd", mb: 0.5 }}>
                  Logo (Upload)
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  sx={{
                    borderColor: "#6c757d",
                    color: "#fff",
                    "&:hover": {
                      borderColor: "#90caf9",
                      backgroundColor: "#2a2f36",
                    },
                  }}
                >
                  Upload Logo
                  <input hidden type="file" accept="image/*" onChange={handleFileChange} />
                </Button>
                {selectedFileName && (
                  <Typography
                    sx={{
                      mt: 1,
                      color: "#adb5bd",
                      fontStyle: "italic",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={selectedFileName}
                  >
                    {selectedFileName}
                  </Typography>
                )}
                <input type="hidden" {...register("logo")} />
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} sx={{ color: "#adb5bd" }}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" sx={{ bgcolor: "#1976d2" }}>
              {editingId ? "Update Client" : "Create Client"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* -------- Client List -------- */}
      <Paper sx={{ bgcolor: "#1e1e1e", p: 2, borderRadius: 2, color: "#fff" }}>
        <Box display="flex" justifyContent="space-between" mb={3} flexWrap={"wrap"}>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            Client List
          </Typography>
          <Box display="flex" alignItems="center" flexWrap={"wrap"} gap={2}>
            <SearchBar
              data={clients}
              searchKeys={["name", "email", "phone", "location", "contactPerson"]}
              onSearch={handleSearch}
              placeholder="Search clients..."
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select
                value={clusterFilter}
                onChange={(e) => handleClusterFilterChange(e.target.value)}
                sx={{ bgcolor: "#343a40", color: "#fff" }}
              >
                {clusterFilterOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box
              display="flex"
              flexWrap={{ xs: "wrap", sm: "nowrap" }}
              gap={1}
              width={{ xs: "100%", sm: "auto" }}
            >
              <Button
                size="small"
                startIcon={<AddIcon />}
                variant="contained"
                onClick={() => {
                  reset({ layoutType: "simple" });
                  setLayout([]);
                  setEditingId(null);
                  setSelectedFileName("");
                  setOpenDialog(true);
                }}
                sx={{
                  bgcolor: "#1976d2",
                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                  flex: { xs: "1 1 auto", sm: "initial" },
                }}
              >
                Create Client
              </Button>
              <Button
                size="small"
                startIcon={<LinkIcon />}
                variant="contained"
                onClick={() => navigate("/daily-catering-order-form")}
                sx={{
                  bgcolor: "#2e7d32",
                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                  flex: { xs: "1 1 auto", sm: "initial" },
                }}
              >
                Order Form
              </Button>
              <Button
                size="small"
                startIcon={<LinkIcon />}
                variant="contained"
                onClick={() => navigate("/daily-catering-order-report")}
                sx={{
                  bgcolor: "#ed6c02",
                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                  flex: { xs: "1 1 auto", sm: "initial" },
                }}
              >
                Order Report
              </Button>
            </Box>
          </Box>
        </Box>

        {displayedClients && displayedClients.length ? (
          <>
            <TableContainer sx={{ backgroundColor: "transparent" }}>
              <Table size="small" sx={{ "& td": { verticalAlign: "top" } }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#8d8787" }}>
                    {[
                      "Sno",
                      "Name",
                      "Email",
                      "Phone",
                      "Designation",
                      "Contact Person",
                      "Location",
                     
                      "Services",
                      "Vendors",
                      "Cluster",
                      "Actions",
                    ].map((header) => (
                      <TableCell
                        key={header}
                        sx={{ fontWeight: "bolder", fontSize: "0.75rem", py: 0.5 }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {displayedClients
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((client, index) => (
                      <TableRow key={client._id} sx={{ height: "32px" }}>
                        <TableCell sx={{ color: "white", fontSize: "0.75rem", py: 0.5 }}>
                          {page * rowsPerPage + index + 1}
                        </TableCell>
                        <TableCell sx={{ color: "white", fontSize: "0.75rem", py: 0.5 , whiteSpace: "nowrap",}}>
                          {client.name || "-"}
                        </TableCell>
                        <TableCell sx={{ color: "white", fontSize: "0.75rem", py: 0.5 }}>
                          {client.email || "-"}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap",color: "white", fontSize: "0.75rem", py: 0.5 }}>
                          {client.phone || "-"}
                        </TableCell>
                        <TableCell sx={{ color: "white", fontSize: "0.75rem", py: 0.5 }}>
                          {client.designation || "-"}
                        </TableCell>
                        <TableCell sx={{ color: "white", fontSize: "0.75rem", py: 0.5 }}>
                          {client.contactPerson || "-"}
                        </TableCell>
                        <TableCell sx={{ color: "white", fontSize: "0.75rem", py: 0.5 }}>
                          {client.location || "-"}
                        </TableCell>
                    
                        <TableCell
                          sx={{
                            color: "white",
                            fontSize: "0.75rem",
                            py: 0.5,
                            maxWidth: 160,
                          }}
                        >
                          {(() => {
                            const servicesText =
                              client.services && client.services.length > 0
                                ? client.services.join(", ")
                                : "-";
                            return (
                              <Tooltip title={servicesText}>
                                <Box
                                  sx={{
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {servicesText}
                                </Box>
                              </Tooltip>
                            );
                          })()}
                        </TableCell>
                        <TableCell
                          sx={{
                            color: "white",
                            fontSize: "0.75rem",
                            py: 0.5,
                            maxWidth: 160,
                          }}
                        >
                          {(() => {
                            const vendorText =
                              client.vendor && client.vendor.length > 0
                                ? client.vendor
                                    .map((v) => (typeof v === "string" ? v : v.name))
                                    .join(", ")
                                : "-";
                            return (
                              <Tooltip title={vendorText}>
                                <Box
                                  sx={{
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {vendorText}
                                </Box>
                              </Tooltip>
                            );
                          })()}
                        </TableCell>
                        <TableCell sx={{ color: "white", fontSize: "0.75rem", py: 0.5, maxWidth: 160, whiteSpace: "nowrap",}}>
                          {client.cluster
                            ? clusterOptions.find((opt) => opt.value === client.cluster)
                                ?.label || client.cluster
                            : "-"}
                        </TableCell>
                        <TableCell sx={{ py: 0.5,display:"flex", }} align="center">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEdit(page * rowsPerPage + index)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(page * rowsPerPage + index)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box
              className="mui-wrapper mui-isolated-container p-0"
              sx={{
                color: "white",
                px: 2,
                py: 1,
                display: "flex",
                justifyContent: "flex-end",
                borderTop: "1px solid #ccc",
              }}
            >
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={displayedClients.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
                sx={{
                  color: "#fff",
                  bgcolor: "#212529",
                  ".MuiTablePagination-toolbar": {
                    color: "white",
                    overflow: { xs: "scroll", sm: "hidden" },
                  },
                  ".MuiTablePagination-actions": { color: "#90caf9" },
                  ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
                    { color: "#adb5bd" },
                }}
              />
            </Box>
          </>
        ) : (
          <Typography sx={{ textAlign: "center", mt: 4 }}>No clients found.</Typography>
        )}
      </Paper>
    </Stack>
  );
};

export default Client;
